import React from 'react'
import Link from 'next/link'
import dayjs from 'dayjs'
import Comments from '@/components/News/Comments'
import { newsCategoryLabel } from '@/utils/news'
import { BRAND, ORG_ID, SITE_NAME, absUrl, breadcrumbLd, plainText, SeoProps } from '@/utils/seo'

// 公开图文阅读页(可分享)。/post/edit 与 /post 为静态路由,优先级更高,不冲突。
// 同时承载两类内容:活动(Activity)与资讯(Post);isNews 为真时才显示阅读量/分类/评论区。
type Post = {
  id: string
  title: string
  cover?: string
  body_html?: string
  description?: string
  fee?: number
  owner_name?: string
  published_at?: string
  created_at?: string
  isNews?: boolean
  category?: string
  tags?: string[]
  view_count?: number
  comment_count?: number
  short_id?: string
  updated_at?: string
  related?: { id: string; short_id?: string; title: string }[]
}

const PostReader: any = ({ post }: { post: Post }) => {
  if (!post) return null
  return (
    <>
      <div className="mx-auto max-w-2xl px-4 py-6">
        {post.isNews && (
          <div className="mb-3 text-xs">
            <Link href="/news" className="text-gray-400 no-underline hover:text-[#DB3142]">
              ← 返回资讯
            </Link>
          </div>
        )}
        <h1 className="text-2xl font-bold leading-snug text-gray-900">{post.title}</h1>
        {post.isNews && post.description && <p className="mt-3 text-sm leading-relaxed text-gray-500">{post.description}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 text-xs text-gray-400">
          <span>{post.owner_name || '灵缇'}</span>
          <span>{dayjs(post.published_at || post.created_at).format('YYYY-MM-DD HH:mm')}</span>
          {post.isNews && (
            <>
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-500">{newsCategoryLabel(post.category)}</span>
              {/* show 返回的 view_count 已是自增后的值,直接显示(与 /news 详情口径一致) */}
              <span>阅读 {post.view_count ?? 0}</span>
              <span>评论 {post.comment_count ?? 0}</span>
            </>
          )}
          {post.fee && post.fee > 0 ? <span className="ml-auto font-bold text-[#f83232]">报名费 ¥{(post.fee / 100).toFixed(0)}</span> : null}
        </div>
        {post.cover && <img src={post.cover} alt={post.title} className="mt-4 w-full rounded-lg" />}
        {post.fee && post.fee > 0 ? (
          <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-[#f83232]">本活动报名费 ¥{(post.fee / 100).toFixed(0)} —— 请在小程序内打开报名并微信支付。</div>
        ) : null}
        <style
          dangerouslySetInnerHTML={{
            __html:
              '.post-body img{max-width:100%;height:auto;display:block;margin:14px auto;border-radius:6px}.post-body p{margin:8px 0}.post-body section{margin:0}',
          }}
        />
        <div
          className="post-body mt-5"
          style={{ lineHeight: 1.85, color: '#222', fontSize: 16, wordBreak: 'break-word' }}
          dangerouslySetInnerHTML={{ __html: post.body_html || '' }}
        />
        {post.isNews && (post.tags ?? []).length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {(post.tags ?? []).map((t) => (
              <span key={t} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-500">
                #{t}
              </span>
            ))}
          </div>
        )}
        {post.isNews && <Comments postId={post.id} />}
        {(post.related ?? []).length > 0 && (
          <nav aria-label="相关资讯" className="mt-10 border-t border-solid border-gray-100 pt-6">
            <h2 className="mb-3 text-base font-semibold text-gray-900">更多资讯</h2>
            <ul className="flex flex-col gap-2">
              {post.related.map((r) => (
                <li key={r.id}>
                  <a href={`/post/${r.short_id || r.id}`} className="text-sm text-gray-600 hover:text-[#DB3142]">
                    {r.title}
                  </a>
                </li>
              ))}
            </ul>
            <Link href="/news" className="mt-4 inline-block text-sm text-[#DB3142]">
              查看全部资讯 →
            </Link>
          </nav>
        )}
      </div>
    </>
  )
}

// 这个路由同时承载两类内容:活动(Activity)与图文(Post)。先按活动查,查不到再按图文查
// (图文既支持 uuid 也支持 short_id)。此前只查活动,导致 /admin/posts 里的预览链接和
// 所有对外分享的图文链接在 web 上一律 404,只能在小程序里看。
export const getServerSideProps = async (ctx: any) => {
  const id = ctx.params?.id
  const apiBase = process.env.NEXT_PUBLIC_API_URL
  const fetchJson = async (path: string) => {
    try {
      return await (await fetch(`${apiBase}${path}`)).json()
    } catch {
      return null
    }
  }

  const act = await fetchJson(`/users/activities/${id}`)
  if (act?.ok && act.activity) {
    const a = act.activity
    const seo: SeoProps = {
      title: `${a.title} · ${BRAND}`,
      description: (a.description || plainText(a.body_html, 150) || a.title).slice(0, 160),
      image: a.cover || undefined,
      path: `/post/${id}`,
      type: 'article',
    }
    return { props: { post: a, seo } }
  }

  const doc = await fetchJson(`/users/posts/${id}`)
  if (doc?.ok && doc.post) {
    const { summary, ...rest } = doc.post
    const p = rest
    // 规范地址统一用 short_id(列表/分享都用它),uuid 访问也指向同一个 canonical
    const path = `/post/${p.short_id || p.id}`
    const description = (summary || plainText(p.body_html, 150) || p.title).slice(0, 160)
    const tags: string[] = p.tags || []
    const section = newsCategoryLabel(p.category)
    // 「更多资讯」内链:同栏目的最新几篇,给爬虫和读者一条继续往下走的路
    const list = await fetchJson(`/users/posts?page=0&pageSize=8&category=${encodeURIComponent(p.category || '')}&key=`)
    const related = (list?.posts ?? [])
      .filter((r) => r.id !== p.id)
      .slice(0, 6)
      .map((r) => ({ id: r.id, short_id: r.short_id || null, title: r.title }))
    const seo: SeoProps = {
      title: `${p.title} · 灵缇资讯`,
      description,
      keywords: [...tags, section, BRAND, SITE_NAME].filter(Boolean).join(','),
      image: p.cover || undefined,
      path,
      type: 'article',
      publishedTime: p.published_at || p.created_at,
      modifiedTime: p.updated_at || undefined,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: p.title.slice(0, 110),
          description,
          image: p.cover ? [p.cover] : [absUrl('/lingti-gd-logo.png')],
          datePublished: p.published_at || p.created_at,
          dateModified: p.updated_at || p.published_at || p.created_at,
          author: { '@type': 'Person', name: p.owner_name || BRAND },
          publisher: { '@id': ORG_ID, '@type': 'Organization', name: BRAND, logo: { '@type': 'ImageObject', url: absUrl('/lingti-gd-logo.png') } },
          mainEntityOfPage: absUrl(path),
          keywords: tags.join(','),
          articleSection: section,
          inLanguage: 'zh-CN',
          commentCount: p.comment_count ?? 0,
          interactionStatistic: { '@type': 'InteractionCounter', interactionType: 'https://schema.org/ReadAction', userInteractionCount: p.view_count ?? 0 },
        },
        breadcrumbLd([
          ['首页', '/'],
          ['资讯', '/news'],
          [p.title, path],
        ]),
      ],
    }
    ctx.res?.setHeader('Cache-Control', 'public, s-maxage=120, stale-while-revalidate=3600')
    return { props: { post: { ...p, description: summary || '', isNews: true, related }, seo } }
  }

  return { notFound: true }
}

export default PostReader
